import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaFileContract, FaCreditCard, FaBell } from "react-icons/fa";
import ProfileDetails from "./ProfileDetails";
import PaymentInfo from "./PaymentInfo";
import Agreement from "./Agreement";
import Notifications from "./Notifications";
import { createInvitation } from "../../../influencer/services/affiliateService";

const InviteAffiliate = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [affiliateData, setAffiliateData] = useState({
    profile: {},
    agreement: false,
    payment: {},
    notifications: false,
  });

  const steps = [
    { id: 1, name: "Profile Details", icon: <FaUser /> },
    { id: 2, name: "Agreement", icon: <FaFileContract /> },
    { id: 3, name: "Payment Info", icon: <FaCreditCard /> },
    { id: 4, name: "Notifications", icon: <FaBell /> },
  ];

  const validateStep = (step) => {
    switch (step) {
      case 1:
        const { firstName, lastName, email, affiliateType } = affiliateData.profile;
        if (!firstName || !lastName || !email || !affiliateType) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (affiliateType === 'company' && !affiliateData.profile.companyName) {
          toast.error("Company name is required");
          return false;
        }
        if (affiliateType === 'influencer' && !affiliateData.profile.instagramId) {
          toast.error("Instagram ID is required");
          return false;
        }
        return true;

      case 2:
        if (!affiliateData.agreement) {
          toast.error("Please accept the agreement");
          return false;
        }
        return true;

      case 3:
        const { method, skipPayment } = affiliateData.payment;
        if (skipPayment) {
          return true;
        }
        if (method === 'bank') {
          const { accountNumber, bankName, ifscCode } = affiliateData.payment;
          if (method && (!accountNumber || !bankName || !ifscCode)) {
            toast.error("Please fill in all bank details or skip payment information");
            return false;
          }
        }
        if (method === 'upi') {
          const { upiId, upiName, upiPhone } = affiliateData.payment;
          if (method && (!upiId || !upiName || !upiPhone)) {
            toast.error("Please fill in all UPI details or skip payment information");
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;

    // Move to next step
    setActiveStep(prev => Math.min(prev + 1, steps.length));
  };

  const handlePrevious = () => {
    setActiveStep(prev => Math.max(prev - 1, 1));
  };

  const handleSkip = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Extract brandId from the first campaign
      const campaigns = affiliateData.profile.campaigns;
      
      let brandId = null;
      if (Array.isArray(campaigns) && campaigns.length > 0) {
        const firstCampaign = campaigns[0];
        
        // Handle both possible structures: brandId._id or brandId
        if (firstCampaign.brandId && firstCampaign.brandId._id) {
          brandId = firstCampaign.brandId._id;
        } else if (firstCampaign.brandId) {
          brandId = firstCampaign.brandId;
        }
      }

      if (!brandId) {
        toast.error("Please select at least one campaign");
        setLoading(false);
        return;
      }
      // Calculate expiresAt (7 days from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);


      // Prepare the invitation data
      const invitationData = {
        profile: {
          ...affiliateData.profile,
          // Map campaigns array to only include campaign IDs
          campaigns: affiliateData.profile.campaigns.map(campaign => campaign._id)
        },
        payment: affiliateData.payment,
        agreement: affiliateData.agreement,
        notifications: affiliateData.notifications,
        brandId // Add brandId at root level
        // Removed expiresAt
      };

      console.log("🔍 Debug - Final invitationData payload:", JSON.stringify(invitationData, null, 2));

      await createInvitation(invitationData);

      toast.success("Affiliate invited successfully! An invitation email has been sent.", {
        position: "top-center",
        autoClose: 3000,
      });
      navigate('/company/dashboard/affiliates/');
    } catch (error) {
      const response = error?.response?.data?.error;

      if (response?.error === "DUPLICATE_CAMPAIGNS" && response?.alreadyInvitedCampaigns?.length) {
        toast.error(
          `Already invited to: ${response.alreadyInvitedCampaigns.join(", ")}. Please select different campaigns.`,
          { autoClose: 5000 }
        );
      } else {
        toast.error(response?.message || "Failed to create affiliate");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <ProfileDetails
            data={affiliateData.profile}
            setData={(data) => setAffiliateData(prev => ({ ...prev, profile: data }))}
          />
        );
      case 2:
        return (
          <Agreement
            agreed={affiliateData.agreement}
            setAgreed={(agreed) => setAffiliateData(prev => ({ ...prev, agreement: agreed }))}
          />
        );
      case 3:
        return (
          <PaymentInfo
            data={affiliateData.payment}
            setData={(data) => setAffiliateData(prev => ({ ...prev, payment: data }))}
            onSkip={handleSkip}
          />
        );
      case 4:
        return (
          <Notifications
            enabled={affiliateData.notifications}
            setEnabled={(enabled) => setAffiliateData(prev => ({ ...prev, notifications: enabled }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">

        <div className="flex-1 p-2 md:p-4 lg:p-6">
          <div className="bg-white shadow-md rounded-lg p-4 lg:p-6  mb-10 lg:mb-0">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="text-2xl font-bold">Invite New Affiliate</h2>
              {/* <button
                onClick={() => navigate('/company/dashboard/affiliates/marketplace')}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 transition"
              >
                Affiliate Marketplace
              </button> */}
            </div>

            <nav className="text-sm text-gray-500 mb-4">
              <span>Invite affiliate &gt; </span>
              <span className="text-gray-700">
                {steps.find(step => step.id === activeStep)?.name}
              </span>
            </nav>

            <div className="flex flex-col lg:flex-row">
              {/* Steps Section */}
              <div className="w-full lg:w-1/4 mb-4 lg:mb-0 lg:pr-4">
                <div className="space-y-4">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className={`flex items-center space-x-2 ${activeStep === step.id ? "text-orange-600" : "text-gray-400"
                        }`}
                    >
                      <span
                        className={`w-6 h-6 flex items-center justify-center ${activeStep === step.id
                          ? "bg-orange-600 text-white"
                          : "border"
                          } rounded-full`}
                      >
                        {step.icon}
                      </span>
                      <span>{step.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full lg:w-3/4">
                {renderContent()}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-6">
                  <div className="flex space-x-3">
                    {activeStep > 1 && (
                      <button
                        onClick={handlePrevious}
                        disabled={loading}
                        className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Previous
                      </button>
                    )}
                    {activeStep === 3 && (
                      <button
                        onClick={handleSkip}
                        disabled={loading}
                        className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Skip Payment
                      </button>
                    )}
                  </div>
                  <div>
                    {activeStep < steps.length ? (
                      <button
                        onClick={handleNext}
                        disabled={loading}
                        className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-6 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Creating...' : 'Submit'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default InviteAffiliate;


